import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Handshake, CheckCircle, Rocket, Zap, Trophy, ShieldCheck, Globe, Building2 } from 'lucide-react';
import '../styles/MetaPartnerSection.css';

const MetaPartnerSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const benefits = [
    {
      id: 1,
      title: 'API Oficial WhatsApp',
      description: 'Acceso directo a la API oficial de WhatsApp Business con todas las funcionalidades',
      icon: <CheckCircle size={24} />
    },
    {
      id: 2,
      title: 'Soporte Prioritario',
      description: 'Acceso a soporte técnico directo de Meta para resolver cualquier inconveniente',
      icon: <Rocket size={24} />
    },
    {
      id: 3,
      title: 'Nuevas Funcionalidades',
      description: 'Acceso temprano a nuevas características y funcionalidades de WhatsApp Business',
      icon: <Zap size={24} />
    },
    {
      id: 4,
      title: 'Mejores Prácticas',
      description: 'Implementamos las mejores prácticas recomendadas directamente por Meta',
      icon: <Trophy size={24} />
    }
  ];

  return (
    <section className="meta-partner-section section" ref={ref}>
      <div className="container">
        <div className="meta-content">
          <motion.div 
            className="meta-text"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="meta-badge">
              <motion.div 
                className="badge-icon"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Handshake size={20} />
              </motion.div>
              <span className="badge-text">Socios Oficiales</span>
            </div>

            <h2>
              Somos <span className="text-gradient">Meta Business Partners</span>
            </h2>
            
            <p className="meta-description">
              Como socios oficiales de Meta, Runamatic tiene acceso directo a las APIs más avanzadas 
              de WhatsApp Business, garantizando la máxima confiabilidad, seguridad y performance 
              para tu negocio.
            </p>

            <div className="benefits-list">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.id}
                  className="benefit-item"
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ x: 10 }}
                >
                  <div className="benefit-icon">{benefit.icon}</div>
                  <div className="benefit-content">
                    <h4 className="benefit-title">{benefit.title}</h4>
                    <p className="benefit-description">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div 
              className="meta-cta"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.a
                href="https://app.runamatic.io/en/login"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Comienza Ahora
              </motion.a>
            </motion.div>
          </motion.div>

          <motion.div 
            className="meta-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="partnership-visual">
              <motion.div 
                className="meta-partner-image"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                <img 
                  src="/metaPartner.png" 
                  alt="Meta Business Partner Certificate" 
                  className="partner-certificate"
                />
                <div className="certificate-glow"></div>
              </motion.div>

              <motion.div
                className="partner-credentials"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <div className="credential-item">
                  <ShieldCheck size={16} />
                  <span>WhatsApp Cloud API</span>
                </div>
                <div className="credential-item">
                  <Building2 size={16} />
                  <span>500+ Empresas</span>
                </div>
                <div className="credential-item">
                  <Globe size={16} />
                  <span>Latinoamérica</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MetaPartnerSection; 